import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { dashboardAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const NotificationPanel = ({ onClose }) => {
  const queryClient = useQueryClient();

  // 获取通知列表
  const { data, isLoading, error } = useQuery(
    'notifications',
    () => dashboardAPI.getNotifications()
  );

  // 标记通知为已读
  const markAsReadMutation = useMutation(
    (notificationId) => dashboardAPI.markNotificationRead(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
      }
    }
  );

  // 标记所有通知为已读
  const markAllAsReadMutation = useMutation(
    () => dashboardAPI.markAllNotificationsRead(),
    {
      onSuccess: () => {
        toast.success('所有通知已标记为已读');
        queryClient.invalidateQueries('notifications');
      }
    }
  );

  const notifications = data?.notifications || [];

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'danger':
        return 'fa-exclamation-circle text-red-500';
      case 'warning':
        return 'fa-exclamation-triangle text-yellow-500';
      case 'success':
        return 'fa-check-circle text-green-500';
      default:
        return 'fa-info-circle text-blue-500';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'danger':
        return 'bg-red-100';
      case 'warning':
        return 'bg-yellow-100';
      case 'success':
        return 'bg-green-100';
      default:
        return 'bg-blue-100';
    }
  };

  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), {
        addSuffix: true,
        locale: zhCN,
      });
    } catch (error) {
      return '时间未知';
    }
  };

  const handleMarkAsRead = (notificationId) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    if (notifications.length > 0) {
      markAllAsReadMutation.mutate();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* 背景遮罩 */}
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
      
      {/* 通知面板 */}
      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="w-80 md:w-96 relative flex-1 flex flex-col bg-white shadow-xl">
          {/* 面板头部 */}
          <div className="flex-shrink-0 px-4 py-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">通知</h2>
              <div className="flex items-center space-x-2">
                {notifications.length > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-sm text-primary hover:text-blue-700"
                    disabled={markAllAsReadMutation.isLoading}
                  >
                    {markAllAsReadMutation.isLoading ? '处理中...' : '全部已读'}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          {/* 通知列表 */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="spinner mx-auto"></div>
                  <p className="mt-2 text-gray-500">加载通知中...</p>
                </div>
              </div>
            ) : error ? (
              <div className="p-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="fa fa-exclamation-circle text-red-500 mr-3"></i>
                    <div>
                      <p className="text-red-800 font-medium">加载失败</p>
                      <p className="text-red-600 text-sm mt-1">无法加载通知</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6">
                <i className="fa fa-bell-slash text-gray-300 text-4xl mb-3"></i>
                <p className="text-gray-500">暂无通知</p>
                <p className="text-gray-400 text-sm mt-1">所有通知都已处理</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${getNotificationColor(notification.type)}`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <i className={`fa ${getNotificationIcon(notification.type)} text-lg`}></i>
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </p>
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                            disabled={markAsReadMutation.isLoading}
                          >
                            标记已读
                          </button>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">
                          {notification.message}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 面板底部 */}
          <div className="flex-shrink-0 border-t border-gray-200 p-4">
            <div className="text-center">
              <button
                onClick={() => {
                  toast.success('功能开发中...');
                }}
                className="text-sm font-medium text-primary hover:text-blue-700"
              >
                查看所有通知
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;